import {all, call, put, takeLatest, takeLeading} from 'redux-saga/effects';
import {actions, ActionType} from './action';
import {
  getEventComments,
  getEventModuleMapV4,
  getIsExpiredEvent,
} from './apiGet';
import {getAccessToken} from '../../auth/common';
import {
  callApiGetComments,
  callApiPostComment,
} from '../../comment/state/callApi';
import {pAlert} from '../../common/component/PAlert';
import {IS_CLIENT, REGION} from '../../common/constant';
import {pToast} from '../../common/container/PToast';
import {
  convertStrToUint8Array,
  decrypt,
  importKey,
  sha256,
  showAuthModal,
} from '../../common/function';
import {selectT} from '../../common/i18n';
import callApi from '../../common/util/api';
import {makeFetchSaga} from '../../common/util/fetch';
import {formatNumber} from '../../common/util/number';
import {typedSelect} from '../../common/util/saga';
import {callApiContentLike} from '../../commonSub/state/callApi';
import {actions as giftActions} from '../../gift/state/action';
import {sendEventPageEvent} from '../hook/useEventPageEvent';
import {IMissionActionName, IQuizModuleV4} from '../type';

function* fetchEventModuleMap(
  action: ReturnType<typeof actions.fetchEventModuleMap>
) {
  const {eventId} = action.payload;
  const {success, data} = yield call(callApi, {
    url: '/gift/v3/views/event',
    params: {eventId},
  });

  if (success && data) {
    const t = yield* selectT();
    const moduleMap = getEventModuleMapV4({data, eventId, t});
    const commentModule = moduleMap.moduleList?.find(
      item => item.moduleType === 'comment'
    );
    yield put(actions.setEventModuleMapV4(moduleMap));
    if (commentModule && IS_CLIENT) {
      yield put(actions.fetchEventComment(eventId, 'createdDt'));
    }
  }
}

function* fetchEventComment(
  action: ReturnType<typeof actions.fetchEventComment>
) {
  const {eventId, sort} = action.payload;
  const {success, data} = yield callApiGetComments({
    relationId: eventId,
    relationType: 'EVENT',
    sort,
    limit: 3,
  });

  if (success && data) {
    const t = yield* selectT();
    yield put(actions.setEventComments(getEventComments(data, t), sort));
  }
}

function* fetchPostEventComment(
  action: ReturnType<typeof actions.fetchPostEventComment>
) {
  const {eventId, content} = action.payload;
  const success = yield* callApiPostComment({
    relationId: eventId,
    text: content,
    relationType: 'EVENT',
    parentCommentId: 0,
    spoiling: false,
  });

  if (success) {
    // 작성한 댓글을 바로 확인 가능하도록 항상 최신순으로 갱신
    yield put(actions.fetchEventComment(eventId, 'createdDt'));
  }
}

function* fetchContentSubscribe(
  action: ReturnType<typeof actions.fetchContentSubscribe>
) {
  const {id, isOn, index} = action.payload;
  const {success} = yield* callApiContentLike({
    id,
    isOn,
    isEventPage: true,
  });
  if (success) {
    yield put(actions.setContentSubscribe(isOn, index));
  }
}

function* fetchEventCollect(
  action: ReturnType<typeof actions.fetchEventCollect>
) {
  const {param, index} = action.payload;
  const {actionName, customProps} = param;
  const questCampaignId = yield* typedSelect(
    state => state.event.moduleMap.event?.commonModule.questCampaignId
  );
  const {success, data} = yield call(callApi, {
    url: '/gift/v1/available-event',
    params: {questCampaignId},
  });
  if (success && data) {
    if (getIsExpiredEvent(data)) {
      pToast(t => t('error_event_expired_toast'));
      return;
    }
  } else {
    return;
  }
  const user = yield* typedSelect(state => state.auth.user);
  const lang = yield* typedSelect(state => state.common.lang);
  // https://podotree.atlassian.net/wiki/spaces/KW/pages/1570963972/Event+Collector+APIs
  const {success: success2, data: data2} = yield call(callApi, {
    method: 'POST',
    url: '/events-collect/v1/event/collect/mission',
    data: {
      action: {
        type: 'Event',
        name: actionName,
      },
      common: {
        section: 'event_page',
        page: 'event_page',
      },
      custom: {
        region: REGION,
        user_language: lang,
      },
      user_ex_account: {
        user_id: user?.id,
        auth_token: getAccessToken(),
      },
      custom_props: {
        user_action: 'click',
        quest_campaign_id: questCampaignId,
        ...customProps,
      },
    },
  });
  if (success2 && data2) {
    if (actionName === 'quiz_answer') {
      const t = yield* selectT();
      const reward = yield* typedSelect(
        state =>
          (
            state.event.moduleMap.event?.moduleList?.find(
              item => item.moduleType === 'missionQuiz'
            ) as IQuizModuleV4
          ).reward
      );
      reward &&
        pAlert({
          title: t(
            reward.type === 'raffle'
              ? 'crm_quiz_correct_answer_raffle_popup'
              : 'crm_quiz_correct_answer_popup',
            {
              reward:
                reward.type === 'cash'
                  ? t('common_cash_amount', {
                      value: formatNumber(reward.quantity),
                    })
                  : reward.type === 'luckyDraw'
                    ? t('common_crm_slot_ticket')
                    : reward.type === 'ticket'
                      ? t('common_ticket_gift')
                      : undefined,
            }
          ),
        });
    } else {
      const message = MISSION_COMPLETE_TEXT_KEY_MAP[actionName];
      message && pToast(t => t(message));
    }
    yield put(actions.setMissionComplete(actionName, index));
    yield put(giftActions.setValue('needRefresh', true));
  } else {
    pToast(t => t('error_api_call_toast'));
  }
}

function* fetchQuizAnswer(action: ReturnType<typeof actions.fetchQuizAnswer>) {
  const [userId, answer] = yield* typedSelect(state => [
    state.auth.user?.id,
    (
      state.event.moduleMap.event?.moduleList?.find(
        item => item.moduleType === 'missionQuiz'
      ) as IQuizModuleV4
    ).answer,
  ]);
  if (!userId || !answer) {
    showAuthModal({isSaveReturnPage: true});
    return;
  }

  const keyStr = `KAKAOWEBTOON${userId}`;
  const hash: ArrayBuffer = yield call(sha256, keyStr);
  const cryptoKey: CryptoKey = yield call(importKey, hash);
  const iv = new Uint8Array([
    1, 10, 78, 62, -18, -78, -12, 92, 31, 22, 12, 55, -27, 27, 60, 22,
  ]);
  const resultKey: ArrayBuffer = yield call(decrypt, {
    data: convertStrToUint8Array(atob(answer)),
    cryptoKey,
    iv,
  });
  const enc = new TextDecoder('utf-8');
  const realAnswer = enc.decode(resultKey);
  const keyword = action.payload.keyword;
  // 대소문자 구분이 없고 공백을 모두 제거한 상태로 값을 비교해야 한다.
  // https://podotree.atlassian.net/wiki/spaces/KW/pages/2294448561#1-5-%EC%A0%95%EB%8B%B5-%EC%A0%9C%EC%B6%9C-%EB%B2%84%ED%8A%BC
  const isCorrect =
    keyword.toLowerCase().replace(/\s/g, '') ===
    realAnswer.toLowerCase().replace(/\s/g, '');

  const questCampaignId = yield* typedSelect(
    state => state.event.moduleMap.event?.commonModule.questCampaignId
  );
  questCampaignId &&
    sendEventPageEvent(questCampaignId, {
      actionName: 'quiz_answer',
      customProps: {
        is_correct: isCorrect ? 'Y' : 'N',
        answer: keyword,
      },
    });

  if (isCorrect) {
    // 성공일 때에만 collect API 요청하도록 협의봄 (06.30)
    yield put(
      actions.fetchEventCollect({
        actionName: 'quiz_answer',
        customProps: {answer: realAnswer.trim()},
      })
    );
  } else {
    yield call(pAlert, {title: t => t('crm_quiz_incorrect_answer_popup')});
  }
}

const MISSION_COMPLETE_TEXT_KEY_MAP: {
  [key in IMissionActionName]?: string;
} = {
  attendance_check: 'crm_attendance_status_toast',
  attendance_booking: 'crm_attendance_advance_registered_toast',
  content_booking: 'crm_advance_reservation_toast',
};

export default function* saga() {
  yield all([
    takeLatest(
      ActionType.FetchEventModuleMap,
      makeFetchSaga({
        fetchSaga: fetchEventModuleMap,
        canCache: false,
      })
    ),
    takeLatest(
      ActionType.FetchEventComment,
      makeFetchSaga({fetchSaga: fetchEventComment, canCache: false})
    ),
    takeLatest(
      ActionType.FetchPostEventComment,
      makeFetchSaga({fetchSaga: fetchPostEventComment, canCache: false})
    ),
    takeLeading(
      ActionType.FetchContentSubscribe,
      makeFetchSaga({fetchSaga: fetchContentSubscribe, canCache: false})
    ),
    takeLeading(
      ActionType.FetchEventCollect,
      makeFetchSaga({fetchSaga: fetchEventCollect, canCache: false})
    ),
    takeLeading(
      ActionType.FetchQuizAnswer,
      makeFetchSaga({fetchSaga: fetchQuizAnswer, canCache: false})
    ),
  ]);
}
